import { ScopeLevel } from 'constants/ScopeLevel';
import { TFunction } from 'i18next';
import { CONTRACT_COMPLIANCE__MINUTES_SHORT, CONTRACT_COMPLIANCE__NO_ACCESS } from 'translations/constants';
import { IRuleException, ParsedContractComplianceError } from 'types/contract-compliance/IContractCompliance';
import { DOCUMENT_EXTENSIONS, DOCUMENT_MAX_BYTES, SCOPE_LEVEL_LABEL_KEYS } from './constants';

/** 270 → "4h30", 360 → "6h00" (mockup format). */
export const formatHoursMinutes = (minutes: number): string =>
  `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;

/** 30 → "30 min". */
export const formatMinutes = (t: TFunction, minutes: number): string =>
  t(CONTRACT_COMPLIANCE__MINUTES_SHORT, { minutes });

/** Whole, non-negative minutes, or null for empty / non-integer input. */
export const parseMinutesInput = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
};

/** Agency rows have no name; joint commission rows carry the PC number. */
export const getScopeEntityLabel = (t: TFunction, exception: IRuleException): string =>
  exception.scopeLevel === ScopeLevel.AGENCY
    ? t(SCOPE_LEVEL_LABEL_KEYS[ScopeLevel.AGENCY])
    : exception.scopeEntityName ?? '';

/**
 * Backend contract: `message` is already translated; fall back to translating `error`.
 * ModelState 400s, timeouts and 5xx have no `error` and get `fallbackKey`.
 */
export const getContractComplianceErrorMessage = (
  t: TFunction,
  parsed: ParsedContractComplianceError,
  fallbackKey: string,
): string => {
  if (parsed.status === 403) return t(CONTRACT_COMPLIANCE__NO_ACCESS);
  if (parsed.message) return parsed.message;
  if (parsed.key) return t(parsed.key, parsed.details);
  return t(fallbackKey);
};

/** Checked before uploading, so a file the API would reject (type or size) is never sent. */
export const isAllowedJustificationDocument = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return file.size <= DOCUMENT_MAX_BYTES && DOCUMENT_EXTENSIONS.some(extension => name.endsWith(extension));
};

/** details.scopeEntityIds on DUPLICATE_EXCEPTION / SCOPE_ENTITY_NOT_FOUND (sorted by the API). */
export const getOffendingEntityIds = (parsed: ParsedContractComplianceError): number[] => {
  const ids = parsed.details.scopeEntityIds;
  return Array.isArray(ids) ? ids.filter((id): id is number => typeof id === 'number') : [];
};
